/**
 * ************************************************************
 * convert degrees to radians or radians to degrees
 * normalize angle to -180 to +180
 * ************************************************************
 *  
 */
export const deg2rad = (d) => (d * Math.PI) / 180;
export const rad2deg = (r) => (r * 180) / Math.PI;

export const norm180 = (d) => {
  let x = d % 360;
  if (x < -180) x += 360;
  if (x > 180) x -= 360;
  return x;
};
export const norm360 = (d) => {
  let x = d % 360;
  if (x < 0) x += 360;
  return x;
};
// normalize a string to uppercase and trim whitespace
export const normalizeFt = (s) => String(s || "").trim().toUpperCase();

/**
 * ----------Interpolation function --------------
 * ratio used in the linear interpolation.
 * alt1, alt2, alt3 are the winds-aloft altitude levels (ft)
 * ? ternary operator is used to check for a null or undefined value.
 * : if null or undefined then return 0, else do the interpolation calculation.
 * if null then return 0. if not null then interpolate.
 *
 * @param {number} cruiseAlt - The cruise altitude.
 */
export function interpolation(cruiseAlt, alt1, alt2, alt3, v1, v2, v3) {
  cruiseAlt = Number(cruiseAlt);
  alt1 = Number(alt1);
  alt2 = Number(alt2);
  alt3 = Number(alt3);

  v1 = Number(v1);
  v2 = Number(v2);
  v3 = Number(v3);

  if (!Number.isFinite(cruiseAlt)) return 0;

  // Below lowest winds-aloft altitude:
  // use the lowest available wind value directly.
  if (cruiseAlt <= alt1) {
    return Number.isFinite(v1) ? v1 : 0;
  }

  // Between altitude 1 and altitude 2
  if (cruiseAlt <= alt2) {
    const ratio = (cruiseAlt - alt1) / (alt2 - alt1);
    return v1 + (v2 - v1) * ratio;
  }

  // Between altitude 2 and altitude 3
  if (cruiseAlt <= alt3) {
    const ratio = (cruiseAlt - alt2) / (alt3 - alt2);
    return v2 + (v3 - v2) * ratio;
  }

  // Above highest winds-aloft altitude:
  // use the highest available wind value directly.
  return Number.isFinite(v3) ? v3 : 0;
}

/**
 * calculate TAS from CAS and altitude using the formula:
 * Use cruiseAltitude for h and standardTempAtFlightLevel (ISA at cruise alt).
 */
export function tasFromCas(casKt, cruiseAltFt) {
  const po = 101325.0; // Pa
  const Lx = 0.0065; // K/m standard lapse rate
  const To = 288.15; // Kelvin, standard temp at sea level
  const gx = 9.80665; // m/s²
  const Mx = 0.0289664; // kg/mol mass of earth's air
  const Rx = 8.31447; // Joules/(mol·K) gas constant

  const h = cruiseAltFt * 0.3048; // meters
  const n = (gx * Mx) / (Rx * Lx);

  const Ux = (Lx * h) / To; // make a constant.
  const p1 = po * Math.pow(1 - Ux, n); // p1 is pressure at flight level

  const standardTempAtFlightLevelC = 15 - (2 * cruiseAltFt) / 1000.0; // °C
  const a1 = Math.sqrt(po / p1);
  const a2 = Math.sqrt((standardTempAtFlightLevelC + 273.15) / To);

  return casKt * a1 * a2;
}

export const fmtFt0 = (v) =>
  Number.isFinite(Number(v)) ? Math.round(Number(v)).toLocaleString() : "";

/**
 * ---------------------------------------------------
 * FORMAT ABSOLUTE VALUE (1 decimal place)
 * Used for wind components (crosswind/headwind)
 * ---------------------------------------------------
 */
export const fmtAbs1 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "---";
  return Math.abs(n).toFixed(1);
};